import { useState } from 'react'
import { APP_NAME, APP_TAGLINE } from '../../utils/constants.js'

const SOURCE_URL = '' // set when the GitHub repo is made public
const LAST_UPDATED = '2026-10-26' // bump on copy edits to the modals below

// Exported so the standalone /privacy and /terms route pages (UX31)
// render the same source. Google OAuth app verification requires public,
// stable URLs for the Privacy Policy and Terms, so we expose them as
// routed pages in addition to the in-app modal.
export const SECTIONS = {
  about: {
    label: 'About',
    title: `About ${APP_NAME}`,
    body: (
      <>
        <p>
          <strong>{APP_NAME} is Nostr-native civic voting.</strong> Vote Yes,
          No, or Unsure on bills, candidates, and ballot questions at every
          level of US government — federal, state, county, and city. Every
          vote you cast is a cryptographically signed Nostr event: public,
          permanent, and verifiable.
        </p>
        <p>
          We separate <strong>resident</strong> votes from{' '}
          <strong>non-resident</strong> votes on every poll. You see how the
          people who actually live in a jurisdiction are voting, alongside
          the broader national view.
        </p>
        <p>
          {APP_NAME} is non-partisan by design. We do not endorse candidates,
          parties, or causes. We do not sell ads. We have no central
          database — your account lives in your browser and on the public
          Nostr network. You don't need our permission to use the Nostr
          keypair you create here on other Nostr clients.
        </p>
        <p><strong>{APP_TAGLINE}</strong></p>
      </>
    ),
  },
  privacy: {
    label: 'Privacy',
    title: 'Privacy Policy',
    body: (
      <>
        <p>Last updated: {LAST_UPDATED}</p>
        {/* UX58: lead with the mainstream-friendly summary so a user
            scanning the modal gets the plain-English version first.
            The full policy details follow below. */}
        <p className="privacy-lead">
          <strong>Your vote is private.</strong> We track your zip code,
          not your identity. No political texts, no spam calls, no
          personal data sold. You can vote without ever telling us who
          you are.
        </p>
        <p>
          This Privacy Policy explains how {APP_NAME} ("we", "us", or "our")
          processes information about you. It applies when you use any{' '}
          {APP_NAME} product or service, including the {APP_NAME} web
          application at thepeoplevote.org (the "Services").
        </p>

        <p><strong>1. Policy overview</strong></p>
        <p>
          {APP_NAME} doesn't monetize your personal information. We don't
          generate revenue via advertising. We don't sell, rent, or share
          personal information we collect from our users. Our policy is to
          maximize user privacy by collecting only the minimum amount of
          information required to provide the Services and remain compliant
          with applicable laws.
        </p>
        <p>
          We do not operate a backend database. Most of what {APP_NAME} needs
          to function lives in your browser and on the public Nostr relay
          network.
        </p>

        <p><strong>2. Information we collect</strong></p>
        <p>
          <strong>2.1 Account creation.</strong> {APP_NAME} Services enable
          you to create or import an account on the public Nostr network.
          Account creation is anonymous: we do not ask for, and you are not
          required to provide, your real name, email address, phone number,
          or any other personally identifying information. The Nostr public
          key and signed events you publish during normal use are, by the
          nature of the protocol, public and visible to anyone connected to
          the Nostr relays you and we use. This is a normal manner of course
          for all Nostr accounts. When you cast your first vote without
          first creating an account, {APP_NAME} generates a Nostr key for
          you automatically and stores it in your browser so your vote
          can be signed. You can review, export, or discard this
          generated key at any time from your account menu, or save it
          to a Google account via the Save your account prompt.
        </p>
        <p>
          <strong>2.2 Local browser storage.</strong> We store the following
          in your browser's localStorage and sessionStorage, never on a
          server we control:
        </p>
        <ul>
          <li>Your jurisdiction codes (state, city, ZIP, congressional district) so the feed shows you the right polls.</li>
          <li>Your Nostr public key. If you chose in-browser key creation with a passphrase, your private key is also stored locally — encrypted with that passphrase via NIP-49. We never see the unencrypted key.</li>
          <li>A copy of your signed events (polls and votes) so they survive between sessions even when relays drop them.</li>
          <li>Daily snapshot history per poll for the sentiment trend chart.</li>
          <li>A draft of a pending poll proposal, while you are signing in, so the form survives the sign-in round trip.</li>
        </ul>
        <p>
          <strong>2.3 Jurisdiction lookup.</strong> When you save a ZIP code
          to scope your feed, we look it up via the public US Census
          Geocoder to resolve the corresponding state, county, congressional
          district, and city. We keep only the resolved jurisdiction codes;
          we never keep the underlying ZIP-to-address association or any
          street-level data.
        </p>
        <p>
          <strong>2.4 Analytics.</strong> We use Vercel Analytics for basic
          traffic counts (page views, referrers) and, when configured,
          Plausible Analytics for product-funnel events (visit, jurisdiction
          saved, sign-in started, sign-in completed, first vote, referral
          channel). Neither uses cookies. Neither tracks you across the web.
          Neither sees the contents of your votes or proposals.
        </p>
        <p>
          <strong>2.5 Vercel function logs.</strong> Our Vercel-hosted
          serverless and edge functions log the metadata necessary to
          operate the platform: relay accept/reject counts per published
          event, Census Geocoder requests, and OG-meta requests for shared
          poll links. These logs are routine operational telemetry. They
          are not associated with your Nostr identity.
        </p>
        <p>
          <strong>2.6 Support communications.</strong> If you contact us by
          email or social media, we receive whatever information you choose
          to send.
        </p>
        <p>
          <strong>2.7 Google sign-in (optional).</strong> If you choose to
          sign in with Google, {APP_NAME} redirects you to Google's
          authentication service and requests the following scopes:{' '}
          <code>openid</code> and <code>profile</code> to confirm your
          Google identity, and <code>drive.appdata</code> to read and
          write a single application-specific file in your own Google
          Drive. Google returns an authorization code to {APP_NAME},
          which {APP_NAME} exchanges server-side for an ID token. The ID
          token contains your Google user ID (<code>sub</code>);{' '}
          {APP_NAME} uses this and a server-held salt to derive the
          encryption key for your Nostr recovery key, which is stored
          only in the {APP_NAME}-specific Drive <code>appData</code>{' '}
          folder.
        </p>
        <p>
          {APP_NAME} does not store, log, or share your Google email
          address, display name, or any Google Drive files other than
          the ones in our <code>appData</code> folder. The{' '}
          <code>appData</code> folder may contain up to two files: the
          encrypted Nostr recovery key described above, and a plain
          <code>verdant-prefs.json</code> file that stores your saved
          jurisdiction (state, county, city, ZIP, congressional
          district) so it follows you across devices when you sign in
          with Google. The jurisdiction values are already published
          as tags on your signed vote events, so this file is not
          encrypted. You can revoke {APP_NAME}'s Google access at any
          time via your Google Account settings; revoking does not
          delete your Nostr identity, which you can continue to use
          via nsec import.
        </p>
        <p>
          <strong>2.8 Error reporting.</strong> {APP_NAME} uses Sentry to
          report unhandled errors so we can diagnose and fix bugs.
          Captured events include the error message, a stack trace, the
          browser user-agent, and a tag identifying the current operation
          (for example, publishing a vote). When you are signed in,
          Sentry events are associated with your Nostr public key (npub,
          which is public information on the Nostr network); they do not
          include your private key, passphrase, vote contents, or
          profile fields. Errors arising from NIP-07 extension
          rejections (for example, declining a signature prompt) are
          filtered out before sending.
        </p>

        <p><strong>3. How we use your information</strong></p>
        <p>
          We use information collected as described above to operate{' '}
          {APP_NAME}, including filtering the feed to your jurisdiction,
          displaying your votes back to you, computing tallies, generating
          sentiment trend charts, and improving the product.
        </p>

        <p><strong>4. Sharing your information</strong></p>
        <p>
          {APP_NAME} will not share your information with third parties,
          except when required by law. We may share information with law
          enforcement, data protection authorities, government officials,
          and other authorities when: (i) compelled by subpoena, court
          order, or other legal procedure; (ii) we believe disclosure is
          necessary to prevent damage or financial loss; (iii) disclosure
          is necessary to report suspected illegal activity; or (iv)
          disclosure is necessary to investigate violations of our Terms of
          Service or this Privacy Policy.
        </p>
        <p>
          Because {APP_NAME} publishes your events to public Nostr relays
          operated by third parties, those relay operators receive your
          published content as a matter of the protocol. We are not
          responsible for the policies or retention practices of relays we
          do not operate.
        </p>

        <p><strong>5. Your rights and choices</strong></p>
        <ul>
          <li><strong>Accessing and updating your information.</strong> You can review and change your jurisdiction at any time from the Jurisdiction tab. Your Nostr public key is visible in your account menu.</li>
          <li><strong>Deleting your local data.</strong> You can clear all {APP_NAME} data from your browser at any time by clearing site data for thepeoplevote.org. This does not delete events you have already published to Nostr relays — once an event is published, it cannot be withdrawn from a relay's records by us or by you.</li>
          <li><strong>Using a different identity.</strong> If you want a different identity for civic voting than for the rest of your Nostr activity, generate a separate Nostr keypair from the sign-in modal.</li>
        </ul>

        <p><strong>6. Security of your information</strong></p>
        <p>
          We follow industry best practices to protect the integrity of the
          Services. Where in-browser key encryption is used, your private
          key is encrypted with the NIP-49 standard (scrypt +
          xchacha20-poly1305) before being stored. However, no system is
          completely secure. The most important security control is yours:
          keep your Nostr private key safely stored, and do not share it
          with anyone.
        </p>

        <p><strong>7. Changes to this Privacy Policy</strong></p>
        <p>
          We may change this Privacy Policy from time to time. If we make
          changes, we will notify you by revising the date at the top of
          this notice. We encourage you to review this Privacy Policy
          regularly to stay informed about our information handling
          practices and the choices available to you.
        </p>

        <p><strong>8. Contact us</strong></p>
        <p>
          If you have any questions regarding this Privacy Policy, you may
          contact us at support@thepeoplevote.org.
        </p>
      </>
    ),
  },
  terms: {
    label: 'Terms',
    title: 'Terms of Service',
    body: (
      <>
        <p>Last updated: {LAST_UPDATED}</p>
        <p>
          This Agreement is between you and {APP_NAME} ("we", "us", or
          "our"), pertaining to your use of the {APP_NAME} website and
          applications at thepeoplevote.org (the "Services"). By using our
          Services, you agree to be bound by this Agreement. If you don't
          agree to all the stated terms, you may not use our Services.
        </p>
        <p>
          We will periodically revise this Agreement and post the updated
          version, as further described in Section 9 (Changes to Agreement
          or Services).
        </p>

        <p><strong>1. Acceptable Use Policy</strong></p>
        <p>
          <strong>1.1 Eligibility.</strong> By using our Services, you
          represent and warrant that you are at least 18 years of age and
          may legally agree to this Agreement. {APP_NAME} assumes no
          responsibility or liability for any misrepresentation of your
          age.
        </p>
        <p>
          <strong>1.2 Your account rights and responsibilities.</strong>{' '}
          When you create an account via {APP_NAME}, you are assigned a
          Nostr cryptographic key pair: your private key (starting with{' '}
          <code>nsec</code>) and your public key (starting with{' '}
          <code>npub</code>). Your private key grants you full and
          exclusive control over your account on the public Nostr network.
          Your responsibility is to keep your private key secret and safely
          stored. {APP_NAME} assumes no responsibility or liability for
          lost or stolen keys. {APP_NAME} does not have access to your
          private key and cannot restore it if you lose it. You have the
          right to use your Nostr account on third-party Nostr clients. As
          a sovereign owner of your Nostr account, you don't require{' '}
          {APP_NAME}'s permission or cooperation to use it as you see fit.
          If you choose to sign in with Google, your Nostr key is stored
          encrypted in your own Google Drive <code>appData</code> folder;
          see Privacy Policy section 2.7 for details.
        </p>
        <p>
          <strong>1.3 Content ownership.</strong> You retain all ownership
          rights to the content you create. {APP_NAME} does not host your
          content; your events are published to the public Nostr relay
          network and are governed by the protocols and policies of those
          relays.
        </p>
        <p>
          <strong>1.4 Prohibited content.</strong> You agree not to use our
          Services to create, publish, or share any content that is
          illegal, infringing, fraudulent, harmful, threatening, abusive,
          hateful, harassing, defamatory, obscene, or invasive of another's
          privacy. Such content includes, but is not limited to, content
          harmful to minors, pornographic material, violent images, hate
          speech, discriminatory content, and content that promotes
          terrorism or other criminal activities.
        </p>
        <p>
          <strong>1.5 Prohibited conduct.</strong> You agree not to use our
          Services to engage in any conduct that harasses others,
          impersonates others, is intended to intimidate or threaten others,
          or is intended to promote or incite violence. You agree not to
          attempt to disrupt, overload, or manipulate the platform —
          including by spamming polls, ballot-stuffing with multiple
          keypairs, or interfering with the underlying Nostr relays.
        </p>

        <p><strong>2. Privacy notice</strong></p>
        <p>
          Please refer to our Privacy Policy for information on how we
          handle information related to your use of the Services. By using
          the Services, you consent to the information practices set forth
          in the Privacy Policy.
        </p>

        <p><strong>3. Sentiment-only platform</strong></p>
        <p>
          Vote tallies displayed on {APP_NAME} represent{' '}
          <strong>community sentiment</strong> on the public Nostr network.
          They do not represent the outcome of any official election,
          government poll, or referendum. {APP_NAME} makes no guarantee of
          accuracy, completeness, or freedom from manipulation. Anyone with
          a Nostr key can vote, including pseudonymously, multiple times
          via multiple keypairs, and from outside the jurisdiction a poll
          concerns. Use the resident / non-resident split shown on every
          poll as your primary lens, not the headline count.
        </p>

        <p><strong>4. Bot-posted content</strong></p>
        <p>
          Federal and state bills, federal candidate races, and curated
          local races are posted automatically by {APP_NAME}-operated Nostr
          accounts that ingest public records from Congress.gov, LegiScan,
          the Federal Election Commission, and hand-curated sources. Bill
          summaries are extracted from the bill texts themselves. We are
          not affiliated with, endorsed by, or sponsored by the US
          Government, Congress.gov, LegiScan, the FEC, any state
          legislature, or any candidate, campaign, party, or political
          action committee.
        </p>

        <p><strong>5. Cancellation, suspension, or termination of Services</strong></p>
        <p>
          <strong>5.1 Service discontinuation.</strong> We may, in our sole
          discretion, with or without prior notice and at any time, suspend,
          modify, or terminate, temporarily or permanently, all or any
          portion of our Services, with or without reason. Because{' '}
          {APP_NAME} has no central account database, terminating access to
          our applications does not terminate your Nostr account — you may
          continue to use it on any other Nostr client.
        </p>
        <p>
          <strong>5.2 Refusal to display content.</strong> We may refuse to
          display, in the {APP_NAME} user interface, any content we believe
          violates this Agreement or applicable law, without affecting
          whether that content remains on the public Nostr network.
        </p>

        <p><strong>6. Disclaimer of warranty</strong></p>
        <p>
          You acknowledge that our Services are provided on an "as is" and
          "as available" basis without any warranty of any kind, express or
          implied. We make no guarantees that our Services will be
          error-free or run without interruptions, and we do not make any
          warranty regarding the quality, accuracy, reliability, or
          suitability of our Services for any particular purpose. {APP_NAME}{' '}
          is early-development software. Expect bugs, downtime, and
          unexpected behavior.
        </p>

        <p><strong>7. Indemnity</strong></p>
        <p>
          You will indemnify and hold harmless {APP_NAME}, its operator(s),
          and their respective agents from and against any claims, disputes,
          demands, liabilities, damages, losses, and costs and expenses
          (including reasonable legal and accounting fees) arising out of or
          in any way connected with (a) your improper or unauthorized access
          to or use of the Services, and (b) your violation of this
          Agreement.
        </p>

        <p><strong>8. Limitations of liability</strong></p>
        <p>
          <strong>8.1 Consequential damages waiver.</strong> NOTWITHSTANDING
          ANY OTHER SECTION OF THIS AGREEMENT, NEITHER {APP_NAME.toUpperCase()},
          ITS OPERATOR(S), THIRD-PARTY SERVICE PROVIDERS, NOR ANY OTHER PARTY
          INVOLVED IN CREATING, PRODUCING, OR DELIVERING THE SERVICES WILL
          BE LIABLE FOR ANY INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL
          DAMAGES, OR DAMAGES FOR LOST PROFITS, LOST SAVINGS, LOSS OF DATA
          OR GOODWILL, SERVICE INTERRUPTION, COMPUTER DAMAGE OR SYSTEM
          FAILURE, OR THE COST OF SUBSTITUTE SERVICES OF ANY KIND ARISING
          OUT OF OR IN CONNECTION WITH THIS AGREEMENT OR FROM THE USE OF OR
          INABILITY TO USE THE SERVICES, WHETHER BASED ON WARRANTY,
          CONTRACT, TORT (INCLUDING NEGLIGENCE), PRODUCT LIABILITY, OR ANY
          OTHER LEGAL THEORY, AND WHETHER OR NOT {APP_NAME.toUpperCase()} OR
          ANY OTHER PARTY HAS BEEN INFORMED OF THE POSSIBILITY OF SUCH
          DAMAGE. SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR
          LIMITATION OF LIABILITY FOR CONSEQUENTIAL OR INCIDENTAL DAMAGES,
          SO THE ABOVE LIMITATION MAY NOT APPLY TO YOU.
        </p>
        <p>
          <strong>8.2 Liability cap.</strong> NOTWITHSTANDING ANY OTHER
          SECTION OF THIS AGREEMENT, IN NO EVENT WILL{' '}
          {APP_NAME.toUpperCase()}'S TOTAL LIABILITY ARISING OUT OF OR IN
          CONNECTION WITH THIS AGREEMENT OR FROM THE USE OF OR INABILITY TO
          USE THE SERVICES EXCEED ONE HUNDRED U.S. DOLLARS ($100).
        </p>
        <p>
          <strong>8.3 Basis of bargain.</strong> THE EXCLUSIONS AND
          LIMITATIONS OF DAMAGES SET FORTH ABOVE ARE FUNDAMENTAL ELEMENTS
          OF THE BASIS OF THE BARGAIN BETWEEN {APP_NAME.toUpperCase()} AND
          YOU. THE WAIVERS AND LIMITATIONS IN THIS SECTION 8 APPLY
          REGARDLESS OF THE FORM OF ACTION, WHETHER IN CONTRACT, TORT
          (INCLUDING NEGLIGENCE), STRICT LIABILITY, OR ANY OTHER LEGAL
          THEORY, AND WILL SURVIVE AND APPLY EVEN IF ANY LIMITED REMEDY IN
          THESE TERMS FAILS OF ITS ESSENTIAL PURPOSE.
        </p>

        <p><strong>9. Changes to Agreement or Services</strong></p>
        <p>
          We may update this Agreement at any time at our sole discretion.
          If we do so, we will deliver a notice by posting the updated
          Agreement to the {APP_NAME} website at thepeoplevote.org. It is
          important that you review this Agreement regularly for updates.
          If you continue to use the Services after we have posted an
          updated Agreement, you are agreeing to be bound by it. As our
          Services evolve, we may change or discontinue all or any part of
          the Services, at any time and without notice, at our sole
          discretion.
        </p>

        <p><strong>10. Governing law</strong></p>
        <p>
          This Agreement shall be governed by and construed in accordance
          with the laws of the State of Washington, United States, without
          regard to its conflict-of-laws provisions.
        </p>

        <p><strong>11. Acceptance of terms</strong></p>
        <p>
          By using our Services, you signify your acceptance of this
          Agreement. If you do not agree to the terms of this Agreement,
          you may not use our Services.
        </p>

        <p><strong>12. Contact information</strong></p>
        <p>
          If you have any questions regarding this Agreement, you may
          contact us at support@thepeoplevote.org.
        </p>
      </>
    ),
  },
}

export function Footer() {
  const [openSection, setOpenSection] = useState(null)
  const section = openSection ? SECTIONS[openSection] : null

  return (
    <footer className="footer">
      <div className="footer-inner">
        <span className="footer-brand">
          {APP_NAME} · {APP_TAGLINE}
        </span>
        <div className="footer-links">
          {Object.entries(SECTIONS).map(([key, s]) => (
            <button
              key={key}
              type="button"
              className="footer-link"
              onClick={() => setOpenSection(key)}
            >
              {s.label}
            </button>
          ))}
          {SOURCE_URL ? (
            <a
              className="footer-link"
              href={SOURCE_URL}
              target="_blank"
              rel="noreferrer noopener"
            >
              Source
            </a>
          ) : null}
        </div>
      </div>

      {section && (
        <div
          className="modal-backdrop"
          onClick={() => setOpenSection(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <header className="modal-head">
              <h2>{section.title}</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setOpenSection(null)}
                aria-label="Close"
              >×</button>
            </header>
            <div className="modal-body footer-modal-body">
              {section.body}
            </div>
          </div>
        </div>
      )}
    </footer>
  )
}
